/** @module App root: theme, session restore, query client, i18n and router providers. */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { setAuthProvider } from "@/api/client";
import { router } from "@/app/router";
import { accessToken, refresh, restore } from "@/lib/session";
import { useSession } from "@/lib/use-session";
import "@/lib/i18n";
import { applyTheme, readTheme } from "@/lib/theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// The client must not import the session module, so the wiring happens here.
setAuthProvider(accessToken, refresh);

export function App() {
  const session = useSession();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    applyTheme(readTheme());
  }, []);

  useEffect(() => {
    // A reload has no token in memory; the refresh cookie decides whether we stay in.
    void restore().finally(() => {
      setChecked(true);
    });
  }, []);

  useEffect(() => {
    if (checked) void router.invalidate();
  }, [checked, session.status]);

  if (!checked) {
    return <div className="min-h-dvh" aria-busy="true" />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
